import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../utils/apiClient';
import notificationIcon from '../../assets/icons/notifications.svg';
import '../../styles/Notification.css'

const Dropdown = ({ items, onClose }) => {
  const handleClickInside = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="dropdown" onClick={handleClickInside}>
      {items.map((item, idx) => (
        <div
          key={idx}
          className="dropdown-item"
          onClick={(e) => {
            e.stopPropagation();
            item.onClick();
            onClose();
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
};

const NotificationWidget = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadedNotifications, setLoadedNotifications] = useState(false);
  const [filter, setFilter] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !document.querySelector('.dropdown')?.contains(event.target)
      ) {
        setShowNotifications(false);
        setShowFilterDropdown(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showNotifications]);

  const handleBellClick = async () => {
    setShowNotifications((prev) => !prev);

    if (!loadedNotifications) {
      try {
        const res = await apiClient.get(`/notifications`);
        const sorted = res.data
          .map(ntf => ({
            ...ntf,
            CreatedAt: ntf.CreatedAt,
          }))
          .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
        setNotifications(sorted);
        setLoadedNotifications(true);
      } catch (err) {
        console.error('Error loading notifications:', err);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      const unread = notifications.filter(ntf => !ntf.IsRead);

      await Promise.all(
        unread.map(ntf =>
          apiClient.put(`/notifications/${ntf.NotificationId}`, {})
        )
      );

      setNotifications(notifications.map(ntf => ({
        ...ntf,
        IsRead: true
      })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const filteredNotifications = notifications.filter((ntf) => {
    if (filter === 'Unread') return !ntf.IsRead;
    if (filter === 'Read')   return ntf.IsRead;
    return true;
  });

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <button className="header-icon-button" onClick={handleBellClick}>
        <img
          src={notificationIcon}
          alt="Notifications"
          className="notification-icon"
        />
      </button>

      {showNotifications && (
        <div className="notifications-panel">
          <div className="notifications-top-row">
            <div className="notifications-top-left">
              <span className="notifications-top-row-title">Notifications</span>
              <div className="filter-dropdown">
                <button
                  className="filter-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilterDropdown(prev => !prev);
                  }}
                >
                  {filter}
                </button>

                {showFilterDropdown && (
                  <div className="dropdown-container">
                    <Dropdown
                      items={[
                        { label: 'All',    onClick: () => setFilter('All')    },
                        { label: 'Unread', onClick: () => setFilter('Unread') },
                        { label: 'Read',   onClick: () => setFilter('Read')   },
                      ]}
                      onClose={() => setShowFilterDropdown(false)}
                    />
                  </div>
                )}
              </div>
            </div>

            <button className="mark-all-read" onClick={markAllAsRead}>
              Mark all as read
            </button>
          </div>

          <div className="notifications-list">
            {filteredNotifications.length === 0 ? (
              <div className="no-notifications">No notifications found</div>
            ) : (
              filteredNotifications.map((notif, idx) => {
                const isUnread = !notif.IsRead;
                return (
                  <div
                    key={idx}
                    className={`notification-item ${isUnread ? 'notification-item-unread' : ''}`}
                  >
                    <div className="notification-item-title">
                      {notif.Title || notif.Type || 'Untitled'}
                    </div>
                    <div className="notification-item-message">
                      {notif.Message}
                    </div>
                    <div className="notification-item-timestamp">
                      {new Date(notif.CreatedAt).toLocaleString() || 'Unknown time'}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationWidget;